import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  Container,
} from "@mui/material";
import { useQuery, useMutation } from "@tanstack/react-query";
import dataService from "@/app/services/dataService";
import { SHORT_CASHING } from "@/app/constants/constants";
import DownloadHelper from "@/app/helpers/DownloadHelper";
import { useGlobalNotification } from "../header/GlobalNotificationContext";
import { NotificationTypeEnum } from "@/enums/notificationTypeEnum";
import GenericDataDisplay from "../shared/genericDataDisplay";
import SortConfig from "@/app/interfaces/SortConfig";
import SubmissionFilters from "@/app/interfaces/SubbmisionFilter";
import { LoyaltyRebateItem } from "@/app/interfaces/response/LoyaltyRebateItemResponse";
import { Column } from "@/app/interfaces/Column";
import { GenericDataDisplayTypeEnum } from "@/enums/genericDataDisplayType";
import SimpleModal from "../shared/simpleModal";

const DOWNLOAD_ERROR = "An error occurred while generating the PDF.";
const DEFAULT_SORT_FIELD = "monthYear";
const DEFAULT_SORT_ORDER = "desc";
const START_YEAR = 2020;
const MESSAGE_PROCESSING_DOWNLOAD =
  "Your report is being prepared. Feel free to close this dialog while you wait. Please stay on this page to ensure the generation completes successfully.";
const MESSAGE_PROCESSING_DOWNLOAD_TITLE = "Generating Report";

const SubmissionHistory = () => {
  const { setNotification } = useGlobalNotification();

  const [filters, setFilters] = useState<SubmissionFilters>(() => {
    const now = new Date();
    const currentYear = now.getFullYear();

    const defaultYear = now.getMonth() > 0 ? currentYear : currentYear - 1;

    return {
      year: defaultYear,
      vendor: "",
    };
  });

  const [tempFilters, setTempFilters] = useState<SubmissionFilters>(() => {
    const now = new Date();
    const currentYear = now.getFullYear();

    const defaultYear = now.getMonth() > 0 ? currentYear : currentYear - 1;

    return {
      year: defaultYear,
      vendor: "",
    };
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { data: vendorResponse } = useQuery({
    queryKey: ["loyaltyVendors"],
    queryFn: () => dataService.getVendors(),
    staleTime: SHORT_CASHING,
  });

  const fetchSubmissionData = useCallback(
    async (
      page: number,
      pageSize: number,
      currentFilters: SubmissionFilters,
      sort?: SortConfig
    ): Promise<{ items: LoyaltyRebateItem[]; total: number }> => {
      const res = await dataService.getSubmissionHistory({
        page,
        pageSize,
        filter: {
          ...currentFilters,
          customerName: "",
          customerNumber: "",
        },
        sort: sort ? [sort] : [],
      });

      return {
        items: res.success ? res.data.data : [],
        total: res.success ? res.data.totalResults : 0,
      };
    },
    []
  );

  const downloadSummaryMutation = useMutation({
    mutationFn: async ({
      vendor,
      monthYear,
    }: {
      vendor: string;
      monthYear: string;
    }) => {
      const result = await dataService.getRebateDownloadData(vendor, monthYear);
      if (result.success) {
        await DownloadHelper.downloadRebateSummaryPdf({
          vendor,
          monthYear,
          items: result.data,
        });
      } else {
        setNotification(DOWNLOAD_ERROR, NotificationTypeEnum.ERROR);
      }
    },
  });

  const downloadInvoiceMutation = useMutation({
    mutationFn: async ({
      vendor,
      monthYear,
    }: {
      vendor: string;
      monthYear: string;
    }) => {
      const result = await dataService.getRebateInvoiceData(vendor, monthYear);
      if (result.success) {
        await DownloadHelper.downloadQualifyingInvoicesPdf(
          result.data,
          vendor,
          monthYear
        );
      } else {
        setNotification(DOWNLOAD_ERROR, NotificationTypeEnum.ERROR);
      }
    },
  });

  useEffect(() => {
    if (downloadInvoiceMutation.isPending) {
      setIsOpen(true);
    }
  }, [downloadInvoiceMutation.isPending]);

  const columns: Column<LoyaltyRebateItem>[] = [
    {
      id: "monthYear",
      label: "Date Submitted",
      width: "150px",
    },
    {
      id: "vendor",
      label: "Vendor",
      width: "150px",
    },
    {
      id: "SalesSummary",
      label: "Sales Summary",
      width: "180px",
      sortable: false,
      render: (row) => (
        <DownloadAction
          hasData={row.hasEligiblePurchasesForMonth}
          isPending={
            downloadInvoiceMutation.isPending ||
            downloadSummaryMutation.isPending
          }
          onClick={() =>
            downloadSummaryMutation.mutate({
              vendor: row.vendor,
              monthYear: row.monthYear,
            })
          }
        />
      ),
    },
    {
      id: "QualifyingInvoices",
      label: "Qualifying Invoices",
      width: "180px",
      sortable: false,
      render: (row) => (
        <DownloadAction
          hasData={row.hasEligiblePurchasesForMonth}
          isPending={
            downloadInvoiceMutation.isPending ||
            downloadSummaryMutation.isPending
          }
          onClick={() =>
            downloadInvoiceMutation.mutate({
              vendor: row.vendor,
              monthYear: row.monthYear,
            })
          }
        />
      ),
    },
  ];

  const DownloadAction = ({ hasData, isPending, onClick }: any) => {
    if (!hasData)
      return (
        <Typography variant="caption" sx={{ fontStyle: "italic" }}>
          No qualified purchases
        </Typography>
      );
    return (
      <Button
        variant="outlined"
        size="small"
        disabled={isPending}
        onClick={onClick}
        className="darkBlueButtonOutline"
      >
        Download
      </Button>
    );
  };

  const yearOptions = useMemo(() => {
    const years = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const startYear = currentMonth > 0 ? currentYear : currentYear - 1;

    for (let y = startYear; y >= START_YEAR; y--) {
      years.push({ label: y.toString(), value: y });
    }

    return years;
  }, []);

  const vendorOptions = useMemo(
    () =>
      (vendorResponse?.data || []).map((v: any) => ({
        label: v.displayName,
        value: v.name,
      })),
    [vendorResponse]
  );

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        mt: 4,
      }}
    >
      <Box sx={{ py: 2 }}>
        <Typography sx={{ mb: 2 }}>
          Here are records of your files Lansing Building Products has submitted
          to the vendor rebate program you are enrolled in for Loyalty Link.
        </Typography>
        <Typography sx={{ mb: 2 }}>
          <strong>DISCLAIMER:</strong> Lansing Building Products cannot
          guarantee absolute accuracy or be held accountable for reporting
          discrepancies. Only products with valid Lansing sku's can be reported.
          Please review your submissions monthly for complete accuracy.
        </Typography>
        <Box
          sx={{
            p: 2,
            mb: 4,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            gap: 3,
          }}
        >
          <Typography sx={{ fontWeight: "bold" }}>Filters:</Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              flex: 1,
            }}
          >
            <Select
              size="small"
              value={tempFilters.year}
              sx={{
                width: { xs: "100%", sm: 150 },
                backgroundColor: "white",
              }}
              onChange={(e) =>
                setTempFilters({
                  ...tempFilters,
                  year: Number(e.target.value),
                })
              }
            >
              {yearOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <Autocomplete
              size="small"
              options={vendorOptions}
              sx={{ width: { xs: "100%", sm: 250 } }}
              value={
                vendorOptions.find((opt) => opt.value === tempFilters.vendor) ||
                null
              }
              onChange={(_, v) =>
                setTempFilters({ ...tempFilters, vendor: v ? v.value : "" })
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Vendor" />
              )}
            />
          </Box>

          <Box
            sx={{
              ml: { md: "auto" },
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              onClick={() => {
                const reset = { year: tempFilters.year, vendor: "" };
                setTempFilters(reset);
                setFilters(reset);
              }}
              className="darkBlueButtonOutline"
            >
              CLEAR FILTER
            </Button>
            <Button
              variant="contained"
              onClick={() => setFilters(tempFilters)}
              className="darkBlueButtonSolid"
            >
              APPLY FILTER
            </Button>
          </Box>
        </Box>

        <GenericDataDisplay<LoyaltyRebateItem, SubmissionFilters>
          queryKeyBase={["submissionHistory"]}
          externalFilters={filters}
          cashingTime={SHORT_CASHING}
          defaultSort={{
            field: DEFAULT_SORT_FIELD,
            direction: DEFAULT_SORT_ORDER,
          }}
          fetchPage={fetchSubmissionData}
          mode={GenericDataDisplayTypeEnum.TABLE}
          columns={columns}
        />
      </Box>
      <SimpleModal
        isOpen={isOpen}
        message={MESSAGE_PROCESSING_DOWNLOAD}
        title={MESSAGE_PROCESSING_DOWNLOAD_TITLE}
        onClose={() => setIsOpen(false)}
      />
    </Container>
  );
};

export default SubmissionHistory;
